import React, { useState, useEffect } from 'react';
import { Package, Users, TrendingUp, AlertTriangle, Clock, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Item, Transaction, User, UserRole } from '@/types';
import { apiService } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const canViewUsers = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPERADMIN;
  const [stats, setStats] = useState({
    totalItems: 0,
    availableItems: 0,
    checkedOutItems: 0,
    lowStockItems: 0,
    totalUsers: 0,
    recentTransactions: [] as Transaction[]
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [canViewUsers]);

  const fetchDashboardData = async () => {
    try {
      setLoadError(false);

      const [itemsResult, usersResult, transactionsResult] = await Promise.allSettled([
        apiService.get<Item[]>('/items'),
        canViewUsers ? apiService.get<User[]>('/users') : Promise.resolve<User[] | null>(null),
        apiService.get<Transaction[]>('/transactions')
      ]);

      const itemsFailed = itemsResult.status === 'rejected';
      const transactionsFailed = transactionsResult.status === 'rejected';
      const usersFailed = canViewUsers && usersResult.status === 'rejected';

      if (itemsFailed || transactionsFailed) {
        console.error('Dashboard data load failed', {
          items: itemsFailed ? itemsResult.reason : null,
          transactions: transactionsFailed ? transactionsResult.reason : null,
          users: usersFailed && usersResult.status === 'rejected' ? usersResult.reason : null
        });
        setLoadError(true);
      }

      const itemsArray = itemsResult.status === 'fulfilled' ? (itemsResult.value || []) : [];
      const usersArray = usersResult.status === 'fulfilled' && usersResult.value
        ? usersResult.value
        : [];
      const transactionsArray = transactionsResult.status === 'fulfilled'
        ? (transactionsResult.value || [])
        : [];

      const availableItems = itemsArray.filter(item => item.status === 'AVAILABLE').length;
      const checkedOutItems = itemsArray.filter(item => item.status === 'CHECKED_OUT').length;
      const lowStockItems = itemsArray.filter(item => item.quantity <= item.minQuantity).length;

      setStats({
        totalItems: itemsArray.length,
        availableItems,
        checkedOutItems,
        lowStockItems,
        totalUsers: usersArray.length,
        recentTransactions: transactionsArray.slice(0, 5)
      });
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      setLoadError(true);
      setStats({
        totalItems: 0,
        availableItems: 0,
        checkedOutItems: 0,
        lowStockItems: 0,
        totalUsers: 0,
        recentTransactions: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'CHECKOUT': return '📤';
      case 'CHECKIN': return '📥';
      case 'CANCEL': return '❌';
      default: return '📋';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">
          Übersicht über Ihr Inventory Management System
        </p>
      </div>

      {loadError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              <div>
                <h3 className="font-semibold">Fehler beim Laden der Daten</h3>
                <p className="text-sm text-red-600">
                  Die Dashboard-Daten konnten nicht geladen werden. Bitte überprüfen Sie Ihre Verbindung oder melden Sie sich erneut an.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesamt Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalItems}</div>
            <p className="text-xs text-muted-foreground">
              Alle Inventargegenstände
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Verfügbar</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.availableItems}</div>
            <p className="text-xs text-muted-foreground">
              Zur Ausleihe bereit
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ausgeliehen</CardTitle>
            <Activity className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.checkedOutItems}</div>
            <p className="text-xs text-muted-foreground">
              Aktuell verliehen
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Niedriger Bestand</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.lowStockItems}</div>
            <p className="text-xs text-muted-foreground">
              Nachbestellung erforderlich
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Letzte Transaktionen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.recentTransactions.length > 0 ? (
                stats.recentTransactions.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{getTransactionIcon(transaction.type)}</span>
                      <div>
                        <p className="font-medium text-sm">{transaction.item.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {transaction.user ? 
                            transaction.user.name : 
                            'Unbekannter Benutzer'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-xs">
                        {transaction.type}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {format(new Date(transaction.createdAt), 'dd.MM HH:mm', { locale: de })}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  Keine Transaktionen vorhanden
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              System-Übersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {canViewUsers && (
                <div className="flex justify-between items-center">
                  <span className="text-sm">Aktive Benutzer:</span>
                  <Badge variant="default">{stats.totalUsers}</Badge>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm">System-Status:</span>
                <Badge variant="default">Online</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Letzte Synchronisation:</span>
                <span className="text-sm text-muted-foreground">
                  {format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default DashboardPage;
